import React, { useState, useEffect, useCallback } from 'react';
import { createApi, getToken, setToken, clearToken } from './api';

const S = {
  page: { minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  header: {
    background: '#111827', color: '#fff', padding: '14px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  main: { flex: 1, padding: '24px', maxWidth: '1100px', margin: '0 auto', width: '100%' },
  card: { background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: '20px' },
  input: { padding: '9px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.9rem', width: '100%' },
  label: { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: '4px', marginTop: '10px' },
  btn: { padding: '9px 18px', borderRadius: '8px', border: 'none', background: '#111827', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' },
  btnGhost: { padding: '7px 14px', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' },
  btnDanger: { padding: '7px 14px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', fontWeight: 600, cursor: 'pointer', fontSize: '0.8rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' },
  td: { padding: '10px', borderBottom: '1px solid #f1f5f9' },
  badge: (color) => ({ padding: '2px 10px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, background: color + '22', color }),
  error: { background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '12px' },
  success: { background: '#f0fdf4', color: '#15803d', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '12px' },
  tabs: { display: 'flex', gap: '4px', borderBottom: '1px solid #e5e7eb', marginBottom: '18px' },
  tab: (active) => ({
    padding: '9px 16px', border: 'none', background: 'none', cursor: 'pointer',
    fontWeight: active ? 700 : 500, fontSize: '0.85rem',
    color: active ? '#111827' : '#94a3b8',
    borderBottom: active ? '2px solid #111827' : '2px solid transparent',
  }),
};

const STATUS_COLORS = { active: '#16a34a', suspended: '#d97706', provisioning: '#2563eb', failed: '#dc2626' };

const MAX_IMAGEM_BYTES = 300 * 1024; // 300KB — imagem vira base64 salva direto no JSON de configuração do tenant

function lerImagemComoBase64(file) {
  return new Promise((resolve, reject) => {
    if (!file) return resolve('');
    if (file.size > MAX_IMAGEM_BYTES) {
      reject(new Error(`Imagem muito grande (máx. ${Math.round(MAX_IMAGEM_BYTES / 1024)}KB). Use um PNG/SVG mais leve.`));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
    reader.readAsDataURL(file);
  });
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const submeter = async (e) => {
    e.preventDefault();
    setErro(''); setLoading(true);
    try {
      const api = createApi();
      const { data } = await api.post('/master/auth/login', { email, senha });
      setToken(data.token);
      onLogin(data.admin);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao entrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <form onSubmit={submeter} style={{ ...S.card, width: '340px' }}>
        <h2 style={{ margin: '0 0 4px' }}>🛠️ Master</h2>
        <p style={{ margin: '0 0 16px', color: '#64748b', fontSize: '0.85rem' }}>Painel de administração dos tenants</p>
        {erro && <div style={S.error}>{erro}</div>}
        <label style={S.label}>E-mail</label>
        <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
        <label style={S.label}>Senha</label>
        <input style={S.input} type="password" value={senha} onChange={e => setSenha(e.target.value)} required />
        <button style={{ ...S.btn, width: '100%', marginTop: '18px' }} disabled={loading}>
          {loading ? 'Entrando...' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}

function NovoTenantForm({ api, onCriado }) {
  const [form, setForm] = useState({
    nome: '', dominio: '', slug: '', admin_email: '', admin_nome: '',
    configurar_ssl: false, certbot_email: '',
  });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aberto, setAberto] = useState(false);

  const campo = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const criar = async (e) => {
    e.preventDefault();
    setMsg(null); setLoading(true);
    try {
      const { data } = await api.post('/master/tenants', form);
      let texto = `Tenant "${form.nome}" provisionado (id=${data.tenantId}). Já entra com o nome dele, mas vale abrir a aba 🎨 Marca pra subir logo e favicon antes de repassar o acesso.`;
      let tipo = 'success';
      if (form.configurar_ssl) {
        texto += data.ssl?.success
          ? ' SSL configurado com sucesso.'
          : ` ⚠️ Falha ao configurar SSL: ${data.ssl?.error || 'erro desconhecido'} (tente de novo na aba Domínio do tenant, verificando se o DNS já aponta pra este servidor).`;
        if (!data.ssl?.success) tipo = 'error';
      }
      setMsg({ tipo, texto });
      setForm({ nome: '', dominio: '', slug: '', admin_email: '', admin_nome: '', configurar_ssl: false, certbot_email: '' });
      onCriado();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.response?.data?.error || 'Erro ao provisionar tenant' });
    } finally {
      setLoading(false);
    }
  };

  if (!aberto) {
    return <button style={S.btn} onClick={() => setAberto(true)}>+ Novo tenant</button>;
  }

  return (
    <div style={S.card}>
      <h3 style={{ marginTop: 0 }}>Novo tenant</h3>
      {msg && <div style={msg.tipo === 'error' ? S.error : S.success}>{msg.texto}</div>}
      <form onSubmit={criar}>
        <label style={S.label}>Nome do negócio</label>
        <input style={S.input} value={form.nome} onChange={campo('nome')} required />
        <label style={S.label}>Domínio (ex: agenda.cliente.com.br)</label>
        <input style={S.input} value={form.dominio} onChange={campo('dominio')} required />
        <label style={S.label}>Slug (opcional — derivado do nome se vazio)</label>
        <input style={S.input} value={form.slug} onChange={campo('slug')} />
        <label style={S.label}>E-mail do admin do tenant</label>
        <input style={S.input} type="email" value={form.admin_email} onChange={campo('admin_email')} required />
        <label style={S.label}>Nome do admin do tenant</label>
        <input style={S.input} value={form.admin_nome} onChange={campo('admin_nome')} />

        <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type="checkbox"
            checked={form.configurar_ssl}
            onChange={e => setForm(f => ({ ...f, configurar_ssl: e.target.checked }))}
          />
          Configurar Nginx + SSL (certbot) automaticamente
        </label>
        {form.configurar_ssl && (
          <>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '2px 0 0' }}>
              Só funciona se o DNS do domínio já estiver apontando pra este servidor. Se
              falhar, o tenant continua criado normalmente — dá pra tentar de novo depois
              na aba "Domínio" do tenant.
            </p>
            <label style={S.label}>E-mail para o certbot (opcional)</label>
            <input style={S.input} type="email" value={form.certbot_email} onChange={campo('certbot_email')}
              placeholder="usa o e-mail padrão da plataforma se vazio" />
          </>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button style={S.btn} disabled={loading}>{loading ? 'Provisionando...' : 'Criar tenant'}</button>
          <button type="button" style={S.btnGhost} onClick={() => setAberto(false)}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function TenantList({ api, onAbrir }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/master/tenants');
      setTenants(data.tenants);
    } catch (err) {
      setErro(err.response?.data?.error || 'Erro ao carregar tenants');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { carregar(); }, [carregar]);

  const alternarStatus = async (t) => {
    const novo = t.status === 'active' ? 'suspended' : 'active';
    if (!window.confirm(`${novo === 'suspended' ? 'Suspender' : 'Reativar'} o tenant "${t.nome}"?`)) return;
    try {
      await api.patch(`/master/tenants/${t.id}/status`, { status: novo });
      carregar();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao alterar status');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Tenants</h2>
        <NovoTenantForm api={api} onCriado={carregar} />
      </div>
      {erro && <div style={S.error}>{erro}</div>}
      <div style={S.card}>
        {loading ? <p>Carregando...</p> : tenants.length === 0 ? <p style={{ color: '#94a3b8' }}>Nenhum tenant ainda.</p> : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nome</th>
                <th style={S.th}>Domínio</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Criado em</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id}>
                  <td style={S.td}>
                    <button onClick={() => onAbrir(t)} style={{ background: 'none', border: 'none', color: '#2563eb', fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}>
                      {t.nome}
                    </button>
                  </td>
                  <td style={S.td}>{t.dominio}</td>
                  <td style={S.td}>
                    <span style={S.badge(STATUS_COLORS[t.status] || '#64748b')}>{t.status}</span>
                    {t.status === 'failed' && t.erro_provisionamento && (
                      <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '4px' }}>{t.erro_provisionamento}</div>
                    )}
                  </td>
                  <td style={S.td}>{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
                  <td style={S.td}>
                    {(t.status === 'active' || t.status === 'suspended') && (
                      <button style={S.btnGhost} onClick={() => alternarStatus(t)}>
                        {t.status === 'active' ? 'Suspender' : 'Reativar'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CampoSenha({ label, value, onChange, placeholder }) {
  return (
    <>
      <label style={S.label}>{label}</label>
      <input style={S.input} type="password" value={value} onChange={onChange} placeholder={placeholder} />
    </>
  );
}

function AbaIntegracoes({ api, tenantId }) {
  const [email, setEmailCfg] = useState(null);
  const [google, setGoogle] = useState(null);
  const [gcal, setGcal] = useState(null);
  const [totalpass, setTotalpass] = useState(null);
  const [msg, setMsg] = useState(null);

  const carregar = useCallback(async () => {
    const [e, g, c, t] = await Promise.all([
      api.get(`/master/tenants/${tenantId}/config/email`),
      api.get(`/master/tenants/${tenantId}/config/google-oauth`),
      api.get(`/master/tenants/${tenantId}/config/gcal`),
      api.get(`/master/tenants/${tenantId}/config/totalpass`),
    ]);
    setEmailCfg(e.data); setGoogle(g.data); setGcal(c.data); setTotalpass(t.data);
  }, [api, tenantId]);

  useEffect(() => { carregar(); }, [carregar]);

  const aviso = (texto, tipo = 'success') => { setMsg({ texto, tipo }); setTimeout(() => setMsg(null), 4000); };

  const salvarEmail = async () => {
    try {
      await api.put(`/master/tenants/${tenantId}/config/email`, email);
      aviso('✅ E-mail salvo'); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro', 'error'); }
  };

  const testarEmail = async () => {
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/config/email/testar`);
      aviso(data.message);
    } catch (err) { aviso(err.response?.data?.error || 'Erro no teste', 'error'); }
  };

  const salvarGoogle = async () => {
    try {
      await api.put(`/master/tenants/${tenantId}/config/google-oauth`, google);
      aviso('✅ Credenciais Google salvas'); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro', 'error'); }
  };

  const conectarGmail = async () => {
    try {
      const { data } = await api.get(`/master/tenants/${tenantId}/gmail/auth-url`);
      window.open(data.url, 'gmail_oauth', 'width=520,height=650');
    } catch (err) { aviso(err.response?.data?.error || 'Erro', 'error'); }
  };

  const desconectarGmail = async () => {
    try {
      await api.delete(`/master/tenants/${tenantId}/gmail/desconectar`);
      aviso('Gmail desconectado'); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro', 'error'); }
  };

  const salvarGcal = async () => {
    try {
      await api.put(`/master/tenants/${tenantId}/config/gcal`, gcal);
      aviso('✅ Google Calendar salvo'); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro', 'error'); }
  };

  const salvarTotalpass = async () => {
    try {
      await api.put(`/master/tenants/${tenantId}/config/totalpass`, totalpass);
      aviso('✅ Credenciais TotalPass salvas'); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro', 'error'); }
  };

  const testarTotalpass = async () => {
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/config/totalpass/testar`);
      aviso(data.message);
    } catch (err) { aviso(err.response?.data?.error || 'Erro no teste', 'error'); }
  };

  const registrarWebhookTotalpass = async () => {
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/config/totalpass/webhook`);
      aviso(data.message); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro ao registrar webhook', 'error'); }
  };

  const removerWebhookTotalpass = async () => {
    try {
      await api.delete(`/master/tenants/${tenantId}/config/totalpass/webhook`);
      aviso('Webhook removido'); carregar();
    } catch (err) { aviso(err.response?.data?.error || 'Erro ao remover webhook', 'error'); }
  };

  if (!email || !google || !gcal || !totalpass) return <p>Carregando...</p>;

  return (
    <div>
      {msg && <div style={msg.tipo === 'error' ? S.error : S.success}>{msg.texto}</div>}

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>📧 E-mail (envio de notificações)</h3>
        <label style={S.label}>Provider</label>
        <select style={S.input} value={email.provider} onChange={e => setEmailCfg({ ...email, provider: e.target.value })}>
          <option value="smtp">SMTP</option>
          <option value="gmail_api">Gmail API (OAuth)</option>
        </select>
        {email.provider === 'smtp' && (
          <>
            <label style={S.label}>Host</label>
            <input style={S.input} value={email.host} onChange={e => setEmailCfg({ ...email, host: e.target.value })} />
            <label style={S.label}>Porta</label>
            <input style={S.input} value={email.port} onChange={e => setEmailCfg({ ...email, port: e.target.value })} />
            <label style={S.label}>Usuário (e-mail)</label>
            <input style={S.input} value={email.user} onChange={e => setEmailCfg({ ...email, user: e.target.value })} />
            <CampoSenha label="Senha / App Password" value={email.pass} placeholder={email.pass}
              onChange={e => setEmailCfg({ ...email, pass: e.target.value })} />
            <label style={S.label}>Nome do remetente</label>
            <input style={S.input} value={email.from_name} onChange={e => setEmailCfg({ ...email, from_name: e.target.value })} />
          </>
        )}
        {email.provider === 'gmail_api' && (
          <div style={{ marginTop: '10px' }}>
            <p style={{ fontSize: '0.85rem' }}>
              Status: {email.gmail_api.conectado ? `✅ Conectado como ${email.gmail_api.email}` : '❌ Não conectado'}
            </p>
            {email.gmail_api.conectado
              ? <button style={S.btnDanger} onClick={desconectarGmail}>Desconectar Gmail</button>
              : <button style={S.btnGhost} onClick={conectarGmail}>Conectar Gmail</button>}
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button style={S.btn} onClick={salvarEmail}>Salvar</button>
          <button style={S.btnGhost} onClick={testarEmail}>Enviar teste</button>
        </div>
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>🔐 Credenciais Google OAuth (app-level)</h3>
        <label style={S.label}>Client ID</label>
        <input style={S.input} value={google.client_id} onChange={e => setGoogle({ ...google, client_id: e.target.value })} />
        <CampoSenha label="Client Secret" value={google.client_secret} placeholder={google.client_secret}
          onChange={e => setGoogle({ ...google, client_secret: e.target.value })} />
        <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Redirect (Google Calendar, no domínio do tenant): {google.gcal_redirect}</p>
        <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Redirect (Gmail, no domínio do master): {google.gmail_redirect}</p>
        <button style={S.btn} onClick={salvarGoogle}>Salvar credenciais</button>
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>📅 Google Calendar (service account)</h3>
        <label style={S.label}>Calendário alvo (e-mail)</label>
        <input style={S.input} value={gcal.target_calendar} onChange={e => setGcal({ ...gcal, target_calendar: e.target.value })} />
        <label style={S.label}>Service account JSON (cole aqui pra trocar)</label>
        <textarea style={{ ...S.input, height: '90px', fontFamily: 'monospace', fontSize: '0.75rem' }}
          placeholder={gcal.service_account.configurado ? `Configurado (${gcal.service_account.client_email})` : 'Cole o JSON do service account'}
          onChange={e => setGcal({ ...gcal, service_account_json: e.target.value })} />
        <button style={S.btn} onClick={salvarGcal}>Salvar</button>
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>🏋️ TotalPass (Check-in)</h3>
        <label style={S.label}>Ambiente</label>
        <select style={S.input} value={totalpass.environment}
          onChange={e => setTotalpass({ ...totalpass, environment: e.target.value })}>
          <option value="production">Produção</option>
          <option value="staging">Staging (testes)</option>
        </select>
        <CampoSenha label="Partner API Key" value={totalpass.partner_api_key} placeholder={totalpass.partner_api_key}
          onChange={e => setTotalpass({ ...totalpass, partner_api_key: e.target.value })} />
        <CampoSenha label="Place API Key" value={totalpass.place_api_key} placeholder={totalpass.place_api_key}
          onChange={e => setTotalpass({ ...totalpass, place_api_key: e.target.value })} />
        <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
          <button style={S.btn} onClick={salvarTotalpass}>Salvar credenciais</button>
          <button style={S.btnGhost} onClick={testarTotalpass}>Testar conexão</button>
        </div>

        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #334155' }}>
          <p style={{ fontSize: '0.85rem' }}>
            Credenciais: {totalpass.configurado ? '✅ configuradas' : '❌ não configuradas'}<br />
            Webhook: {totalpass.webhook_registrado ? '✅ registrado' : '❌ não registrado'}
            {totalpass.token_valido_ate && <><br />Token válido até: {new Date(totalpass.token_valido_ate).toLocaleString('pt-BR')}</>}
            {totalpass.partner_nome && <><br />Partner: {totalpass.partner_nome} · Unidade: {totalpass.place_nome}</>}
          </p>
          {totalpass.webhook_url && (
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', wordBreak: 'break-all' }}>
              URL do webhook (cadastrada automaticamente na TotalPass): {totalpass.webhook_url}
            </p>
          )}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button style={S.btn} onClick={registrarWebhookTotalpass}>
              {totalpass.webhook_registrado ? 'Atualizar webhook' : 'Registrar webhook'}
            </button>
            {totalpass.webhook_registrado && (
              <button style={S.btnDanger} onClick={removerWebhookTotalpass}>Remover webhook</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AbaBot({ api, tenantId }) {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState('');
  const [msg, setMsg] = useState(null);

  const carregar = useCallback(async () => {
    const { data } = await api.get(`/master/tenants/${tenantId}/bot/config`);
    setCfg(data);
  }, [api, tenantId]);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    try {
      const payload = { ...cfg };
      if (apiKey) payload.api_key = apiKey;
      await api.put(`/master/tenants/${tenantId}/bot/config`, payload);
      setApiKey('');
      setMsg({ tipo: 'success', texto: '✅ Configuração do bot salva' });
      carregar();
    } catch (err) {
      setMsg({ tipo: 'error', texto: err.response?.data?.error || 'Erro' });
    }
  };

  if (!cfg) return <p>Carregando...</p>;

  return (
    <div style={S.card}>
      <h3 style={{ marginTop: 0 }}>🤖 Bot (Anthropic)</h3>
      {msg && <div style={msg.tipo === 'error' ? S.error : S.success}>{msg.texto}</div>}
      <label style={S.label}>
        <input type="checkbox" checked={!!cfg.ativo} onChange={e => setCfg({ ...cfg, ativo: e.target.checked })} /> Bot ativo
      </label>
      <label style={S.label}>Nome do bot</label>
      <input style={S.input} value={cfg.nome_bot || ''} onChange={e => setCfg({ ...cfg, nome_bot: e.target.value })} />
      <CampoSenha label="Anthropic API key" value={apiKey}
        placeholder={cfg.api_key_configurada ? `Configurada (${cfg.api_key})` : 'sk-ant-...'}
        onChange={e => setApiKey(e.target.value)} />
      <label style={S.label}>Modelo</label>
      <input style={S.input} value={cfg.modelo || ''} onChange={e => setCfg({ ...cfg, modelo: e.target.value })} />
      <label style={S.label}>Instruções extras</label>
      <textarea style={{ ...S.input, height: '80px' }} value={cfg.instrucoes_extras || ''}
        onChange={e => setCfg({ ...cfg, instrucoes_extras: e.target.value })} />
      <button style={{ ...S.btn, marginTop: '14px' }} onClick={salvar}>Salvar</button>
    </div>
  );
}

const S_field = { marginBottom: '16px' };
const S_fieldLabel = { display: 'block', fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', marginBottom: '2px' };
const S_fieldHelp = { fontSize: '0.75rem', color: '#94a3b8', marginBottom: '8px', lineHeight: 1.4 };
const S_fieldRow = { display: 'flex', alignItems: 'center', gap: '10px' };
const S_fieldUnit = { fontSize: '0.78rem', color: '#94a3b8', whiteSpace: 'nowrap' };

const NumberField = ({ label, help, value, onChange, unit, hint }) => (
  <div style={S_field}>
    <label style={S_fieldLabel}>{label}</label>
    <div style={S_fieldHelp}>{help}</div>
    <div style={S_fieldRow}>
      <input type="number" style={{ ...S.input, width: '140px' }} value={value}
        onChange={e => onChange(Number(e.target.value))} />
      {unit && <span style={S_fieldUnit}>{unit}</span>}
      {hint && <span style={S_fieldUnit}>{hint}</span>}
    </div>
  </div>
);

const CacheStatRow = ({ label, help, atual, max }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div>
      <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{label}</div>
      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{help}</div>
    </div>
    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#334155', whiteSpace: 'nowrap' }}>{atual}<span style={{ color: '#cbd5e1', fontWeight: 500 }}>/{max}</span></div>
  </div>
);

function AbaSistema({ api, tenantId }) {
  const [stats, setStats] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState(null);

  const carregar = useCallback(async () => {
    const [liveRes, cacheRes, cfgRes] = await Promise.all([
      api.get(`/master/tenants/${tenantId}/sistema/live-stats`),
      api.get(`/master/tenants/${tenantId}/sistema/cache-stats`),
      api.get(`/master/tenants/${tenantId}/sistema/config`),
    ]);
    setStats(liveRes.data);
    setCacheStats(cacheRes.data);
    setConfig(cfgRes.data.config);
    setDefaults(cfgRes.data.defaults);
  }, [api, tenantId]);

  useEffect(() => { carregar(); }, [carregar]);

  const aviso = (texto, tipo = 'success') => { setMsg({ texto, tipo }); setTimeout(() => setMsg(null), 4000); };

  const limparCache = async () => {
    await api.post(`/master/tenants/${tenantId}/sistema/limpar-cache`);
    aviso('✅ Cache limpo com sucesso!');
    carregar();
  };

  const salvarConfig = async () => {
    setSalvando(true);
    try {
      await api.put(`/master/tenants/${tenantId}/sistema/config`, { config });
      aviso('✅ Configurações salvas e aplicadas imediatamente!');
      carregar();
    } catch (err) {
      aviso(err.response?.data?.error || 'Erro ao salvar', 'error');
    } finally {
      setSalvando(false);
    }
  };

  const restaurarPadroes = () => setConfig({ ...defaults });

  if (!stats || !cacheStats || !config) return <p>Carregando...</p>;

  const campo = (k) => (v) => setConfig(c => ({ ...c, [k]: v }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {msg && <div style={msg.tipo === 'error' ? S.error : S.success}>{msg.texto}</div>}

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>⚙️ Sistema</h3>
        <p style={{ fontSize: '0.85rem' }}>Uptime do processo: {Math.floor(stats.uptime / 60)} min</p>
        <p style={{ fontSize: '0.85rem' }}>Requisições na janela atual: {stats.requests} (bloqueadas: {stats.blocked})</p>
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>🗄️ Status do Cache</h3>
        <CacheStatRow label="Sessões Google válidas" atual={cacheStats.googleTokensCached} max={cacheStats.googleCacheMax}
          help="JWTs ainda não expirados em cache — cada aba/dispositivo logado gera 1 entrada" />
        <CacheStatRow label="Total no cache Google" atual={cacheStats.googleTokensTotal} max={cacheStats.googleCacheMax}
          help="Inclui tokens prestes a expirar; limpos automaticamente a cada 5 min" />
        <CacheStatRow label="Sessões locais (senha)" atual={cacheStats.localUsersCached} max={cacheStats.localCacheMax}
          help="Usuários com login por e-mail/senha em cache — TTL configurável abaixo" />
        <button style={{ ...S.btnGhost, marginTop: '14px' }} onClick={limparCache}>🗑️ Limpar Cache</button>
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>🚦 Rate Limiting</h3>
        <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Controla quantas requisições são aceitas por IP em 15 minutos. Valores muito baixos podem
          bloquear usuários legítimos; valores muito altos reduzem a proteção contra abusos.
        </p>
        <NumberField label="Limite geral de requisições" unit="req / 15 min"
          help="Máximo de requisições aceitas por IP a cada 15 minutos (todas as rotas)."
          value={config.generalLimiterMax} onChange={campo('generalLimiterMax')} />
        <NumberField label="Limite de tentativas de login" unit="req / 15 min"
          help="Proteção anti brute-force nas rotas /auth/google e /auth/local."
          value={config.authLimiterMax} onChange={campo('authLimiterMax')} />
      </div>

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>🗄️ Cache de Autenticação</h3>
        <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
          Tokens verificados ficam em memória para evitar chamadas repetidas ao Google e ao banco de
          dados. Ajuste o tamanho e o TTL conforme o número de usuários ativos.
        </p>
        <NumberField label="TTL do cache de usuários locais" unit="ms"
          help="Tempo em ms que o perfil de usuário local fica em memória sem consultar o banco."
          value={config.localCacheTtlMs} onChange={campo('localCacheTtlMs')}
          hint={`≈ ${(config.localCacheTtlMs / 1000).toFixed(0)}s (${(config.localCacheTtlMs / 60000).toFixed(1)} min)`} />
        <NumberField label="Antecipação de expiração do token Google" unit="s"
          help="Segundos antes do vencimento do JWT Google em que o cache é invalidado preventivamente."
          value={config.tokenCacheSlack} onChange={campo('tokenCacheSlack')} />
        <NumberField label="Máx. tokens Google em cache" unit="entradas"
          help="Número máximo de tokens Google verificados mantidos em memória."
          value={config.googleCacheMax} onChange={campo('googleCacheMax')} />
        <NumberField label="Máx. usuários locais em cache" unit="entradas"
          help="Número máximo de perfis de usuários locais mantidos em memória."
          value={config.localCacheMax} onChange={campo('localCacheMax')} />

        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <button style={S.btnGhost} onClick={restaurarPadroes}>Restaurar padrões</button>
          <button style={S.btn} onClick={salvarConfig} disabled={salvando}>{salvando ? 'Salvando...' : '💾 Salvar configurações'}</button>
        </div>
      </div>
    </div>
  );
}

function AbaUsuarios({ api, tenantId }) {
  const [usuarios, setUsuarios] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', perfil: 'aluno', senha: '', definirSenha: false });
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState(null); // usuário sendo editado, ou null

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/master/tenants/${tenantId}/usuarios${busca ? `?q=${encodeURIComponent(busca)}` : ''}`);
      setUsuarios(data);
    } catch {
      setMsg({ tipo: 'error', texto: 'Erro ao carregar usuários' });
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, busca]);

  useEffect(() => { carregar(); }, [carregar]);

  const aviso = (texto, tipo = 'success') => { setMsg({ texto, tipo }); setTimeout(() => setMsg(null), 4000); };

  const perfilParaFlags = (perfil) => ({ is_admin: perfil === 'admin', is_professor: perfil === 'professor' });
  const flagsParaPerfil = (u) => u.is_admin ? 'admin' : u.is_professor ? 'professor' : 'aluno';

  const criar = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefone.trim()) { aviso('Nome e telefone são obrigatórios', 'error'); return; }
    if (form.definirSenha && form.senha.length < 6) { aviso('Senha deve ter no mínimo 6 caracteres', 'error'); return; }
    if (form.definirSenha && !form.email.trim()) { aviso('Login local é feito por e-mail — informe um e-mail para poder usar essa senha', 'error'); return; }
    setCriando(true);
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/usuarios`, {
        nome: form.nome, email: form.email, telefone: form.telefone, ...perfilParaFlags(form.perfil),
        senha: form.definirSenha ? form.senha : undefined,
      });
      aviso(data.message || '✅ Usuário criado!');
      setForm({ nome: '', email: '', telefone: '', perfil: 'aluno', senha: '', definirSenha: false });
      carregar();
    } catch (err) {
      aviso(err.response?.data?.error || 'Erro ao criar usuário', 'error');
    } finally {
      setCriando(false);
    }
  };

  const salvarEdicao = async () => {
    try {
      const { data } = await api.put(`/master/tenants/${tenantId}/usuarios/${editando.id}`, {
        nome: editando.nome, email: editando.email, telefone: editando.telefone,
        ...perfilParaFlags(editando.perfil),
      });
      setUsuarios(usuarios.map(u => u.id === editando.id ? data.user : u));
      setEditando(null);
      aviso('✅ Usuário atualizado!');
    } catch (err) {
      aviso(err.response?.data?.error || 'Erro ao atualizar', 'error');
    }
  };

  const alternarBloqueio = async (u) => {
    if (!window.confirm(`${u.is_banir ? 'Desbloquear' : 'Bloquear'} "${u.nome}"?`)) return;
    try {
      await api.patch(`/master/tenants/${tenantId}/usuarios/${u.id}/bloquear`);
      carregar();
    } catch (err) {
      aviso(err.response?.data?.error || 'Erro ao alterar status', 'error');
    }
  };

  const [modalSenha, setModalSenha] = useState(null); // { id, nome }
  const [novaSenha, setNovaSenha] = useState('');

  const definirSenha = async () => {
    if (novaSenha.length < 6) { aviso('Senha deve ter no mínimo 6 caracteres', 'error'); return; }
    try {
      const { data } = await api.post(`/master/tenants/${tenantId}/usuarios/${modalSenha.id}/definir-senha`, { senha: novaSenha });
      aviso(data.message || '✅ Senha definida!');
      setModalSenha(null);
      setNovaSenha('');
      carregar();
    } catch (err) {
      aviso(err.response?.data?.error || 'Erro ao definir senha', 'error');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {msg && <div style={msg.tipo === 'error' ? S.error : S.success}>{msg.texto}</div>}

      <div style={S.card}>
        <h3 style={{ marginTop: 0 }}>+ Novo usuário</h3>
        <form onSubmit={criar}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.label}>Nome *</label>
              <input style={S.input} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} required />
            </div>
            <div>
              <label style={S.label}>Telefone *</label>
              <input style={S.input} value={form.telefone} onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} required />
            </div>
            <div>
              <label style={S.label}>E-mail {form.definirSenha ? '*' : '(opcional)'}</label>
              <input style={S.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required={form.definirSenha} />
            </div>
            <div>
              <label style={S.label}>Papel</label>
              <select style={S.input} value={form.perfil} onChange={e => setForm(f => ({ ...f, perfil: e.target.value }))}>
                <option value="aluno">Aluno</option>
                <option value="professor">Professor</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>

          <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
            <input type="checkbox" checked={form.definirSenha}
              onChange={e => setForm(f => ({ ...f, definirSenha: e.target.checked }))} />
            Definir senha agora (usuário local, já pronto pra logar)
          </label>
          {form.definirSenha ? (
            <>
              <label style={S.label}>Senha (mín. 6 caracteres)</label>
              <input style={S.input} type="password" value={form.senha}
                onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} placeholder="Senha de acesso" />
              <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>
                O login local é sempre feito por e-mail, por isso ele é obrigatório aqui. Combine a
                senha com o usuário por fora (telefone, WhatsApp, presencial) — ela não é enviada por e-mail.
              </p>
            </>
          ) : (
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>
              Sem senha definida aqui: se e-mail for preenchido, o usuário recebe um link de ativação
              para criar a própria senha. Sem e-mail nem senha, o usuário fica cadastrado mas ainda
              não consegue logar.
            </p>
          )}

          <button style={{ ...S.btn, marginTop: '10px' }} disabled={criando}>{criando ? 'Criando...' : 'Criar usuário'}</button>
        </form>
      </div>

      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0 }}>Usuários ({usuarios.length})</h3>
          <input style={{ ...S.input, width: '220px' }} placeholder="Buscar por nome ou e-mail..."
            value={busca} onChange={e => setBusca(e.target.value)} />
        </div>
        {loading ? <p>Carregando...</p> : usuarios.length === 0 ? <p style={{ color: '#94a3b8' }}>Nenhum usuário.</p> : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Nome</th>
                <th style={S.th}>Contato</th>
                <th style={S.th}>Papel</th>
                <th style={S.th}>Status</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => (
                <tr key={u.id}>
                  <td style={S.td}>{u.nome}</td>
                  <td style={S.td}>{u.email || '—'}<br /><span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{u.telefone || '—'}</span></td>
                  <td style={S.td}>
                    <span style={S.badge(u.is_admin ? '#2563eb' : u.is_professor ? '#16a34a' : '#64748b')}>
                      {u.is_admin ? 'Admin' : u.is_professor ? 'Professor' : 'Aluno'}
                    </span>
                  </td>
                  <td style={S.td}>
                    {u.is_banir
                      ? <span style={S.badge('#dc2626')}>Bloqueado</span>
                      : <span style={S.badge('#16a34a')}>Ativo</span>}
                  </td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button style={S.btnGhost} onClick={() => setEditando({ ...u, perfil: flagsParaPerfil(u) })}>Editar</button>
                      {u.auth_type === 'local' && (
                        <button style={S.btnGhost} onClick={() => setModalSenha({ id: u.id, nome: u.nome })}>
                          {u.password_set ? 'Trocar senha' : 'Definir senha'}
                        </button>
                      )}
                      <button style={u.is_banir ? S.btnGhost : S.btnDanger} onClick={() => alternarBloqueio(u)}>
                        {u.is_banir ? 'Desbloquear' : 'Bloquear'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editando && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...S.card, width: '380px' }}>
            <h3 style={{ marginTop: 0 }}>Editar usuário</h3>
            <label style={S.label}>Nome</label>
            <input style={S.input} value={editando.nome} onChange={e => setEditando(ed => ({ ...ed, nome: e.target.value }))} />
            <label style={S.label}>E-mail</label>
            <input style={S.input} type="email" value={editando.email || ''} onChange={e => setEditando(ed => ({ ...ed, email: e.target.value }))} />
            <label style={S.label}>Telefone</label>
            <input style={S.input} value={editando.telefone || ''} onChange={e => setEditando(ed => ({ ...ed, telefone: e.target.value }))} />
            <label style={S.label}>Papel</label>
            <select style={S.input} value={editando.perfil} onChange={e => setEditando(ed => ({ ...ed, perfil: e.target.value }))}>
              <option value="aluno">Aluno</option>
              <option value="professor">Professor</option>
              <option value="admin">Administrador</option>
            </select>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button style={S.btn} onClick={salvarEdicao}>Salvar</button>
              <button style={S.btnGhost} onClick={() => setEditando(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalSenha && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ ...S.card, width: '340px' }}>
            <h3 style={{ marginTop: 0 }}>Definir senha — {modalSenha.nome}</h3>
            <label style={S.label}>Nova senha (mín. 6 caracteres)</label>
            <input style={S.input} type="password" value={novaSenha} onChange={e => setNovaSenha(e.target.value)} autoFocus />
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>
              Combine a senha com o usuário por fora — ela não é enviada por e-mail.
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button style={S.btn} onClick={definirSenha}>Salvar</button>
              <button style={S.btnGhost} onClick={() => { setModalSenha(null); setNovaSenha(''); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const RESULTADO_CORES = { sucesso: '#16a34a', falha: '#dc2626', bloqueado: '#d97706' };

function AbaLogs({ api, tenantId }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filtros, setFiltros] = useState({ resultado: '', email: '' });
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, ...(filtros.resultado ? { resultado: filtros.resultado } : {}), ...(filtros.email ? { email: filtros.email } : {}) });
      const { data } = await api.get(`/master/tenants/${tenantId}/sistema/login-logs?${params.toString()}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [api, tenantId, page, filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  const totalPaginas = Math.max(1, Math.ceil(total / limit));

  return (
    <div style={S.card}>
      <h3 style={{ marginTop: 0 }}>📋 Log de Login</h3>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <input style={{ ...S.input, width: '220px' }} placeholder="Filtrar por e-mail..."
          value={filtros.email} onChange={e => { setPage(1); setFiltros(f => ({ ...f, email: e.target.value })); }} />
        <select style={{ ...S.input, width: '160px' }} value={filtros.resultado}
          onChange={e => { setPage(1); setFiltros(f => ({ ...f, resultado: e.target.value })); }}>
          <option value="">Todos os resultados</option>
          <option value="sucesso">Sucesso</option>
          <option value="falha">Falha</option>
          <option value="bloqueado">Bloqueado</option>
        </select>
      </div>
      {loading ? <p>Carregando...</p> : logs.length === 0 ? <p style={{ color: '#94a3b8' }}>Nenhum log encontrado.</p> : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Quando</th>
              <th style={S.th}>Usuário</th>
              <th style={S.th}>Tipo</th>
              <th style={S.th}>Resultado</th>
              <th style={S.th}>Motivo</th>
              <th style={S.th}>IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id}>
                <td style={S.td}>{new Date(l.criado_at).toLocaleString('pt-BR')}</td>
                <td style={S.td}>{l.nome || l.email || '—'}</td>
                <td style={S.td}>{l.auth_type || '—'}</td>
                <td style={S.td}><span style={S.badge(RESULTADO_CORES[l.resultado] || '#64748b')}>{l.resultado}</span></td>
                <td style={S.td}>{l.motivo || '—'}</td>
                <td style={S.td}>{l.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '14px' }}>
        <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{total} registro(s) — página {page}/{totalPaginas}</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={S.btnGhost} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</button>
          <button style={S.btnGhost} disabled={page >= totalPaginas} onClick={() => setPage(p => p + 1)}>Próxima →</button>
        </div>
      </div>
    </div>
  );
}

// ── Personalização completa (porta de GerenciadorPersonalizacao.js do tenant) ─
// Mesma UI que o admin do tenant vê em "Personalização", operada aqui pelo
// master. Diferença chave: NÃO mexe no favicon/título/CSS vars do próprio
// painel master (aqui é só edição remota — quem aplica de verdade é o app
// do tenant, ao carregar /api/site/configuracoes).

const MARCA_DEFAULT_CONFIG = {
  cabecalho: { titulo: 'Minha Agenda', icone: '📅', subtitulo: 'Sistema de Agendamento Online', logo_url: '' },
  tipografia: { fonte: 'Inter' },
  cores: {
    primaria: '#0f5a30', primaria_escura: '#106311', fundo_app: '#f8fafc',
    header_fundo: '#ffffff', admin_header_inicio: '#348d52', admin_header_fim: '#0d5724',
  },
  login: {
    background_inicio: '#f0fdf4', background_fim: '#dcfce7', titulo: 'Acessar minha conta',
    descricao: 'Plataforma online de agendamento. Consulte os horários disponíveis, reserve sua vaga, gerencie e cancele agendamentos — tudo pelo celular ou computador.',
  },
  contato: { email_suporte: '' },
  favicon: { emoji: '📅', url: '' },
  calendario: { modo: 'link' },
  layout: { cabecalho_fixo: true, mostrar_footer: true, desktop_max_width: '1400px' },
  mobile: { mostrar_nome_no_menu: true },
};

const MARCA_PALETAS = [
  // ── Claras (8) ──────────────────────────────────────────────────────────────
  { nome: 'Verde Suave',    emoji: '🌿', cores: { primaria: '#4ade80', primaria_escura: '#16a34a', admin_header_inicio: '#86efac', admin_header_fim: '#16a34a' } },
  { nome: 'Azul Suave',     emoji: '💙', cores: { primaria: '#38bdf8', primaria_escura: '#0284c7', admin_header_inicio: '#7dd3fc', admin_header_fim: '#0284c7' } },
  { nome: 'Lilás Suave',    emoji: '💜', cores: { primaria: '#c084fc', primaria_escura: '#9333ea', admin_header_inicio: '#d8b4fe', admin_header_fim: '#9333ea' } },
  { nome: 'Rosa Suave',     emoji: '🌸', cores: { primaria: '#f472b6', primaria_escura: '#db2777', admin_header_inicio: '#f9a8d4', admin_header_fim: '#db2777' } },
  { nome: 'Pêssego Suave',  emoji: '🍑', cores: { primaria: '#fb923c', primaria_escura: '#ea580c', admin_header_inicio: '#fdba74', admin_header_fim: '#ea580c' } },
  { nome: 'Coral Suave',    emoji: '🪸', cores: { primaria: '#f87171', primaria_escura: '#dc2626', admin_header_inicio: '#fca5a5', admin_header_fim: '#dc2626' } },
  { nome: 'Turquesa Suave', emoji: '🩵', cores: { primaria: '#2dd4bf', primaria_escura: '#0d9488', admin_header_inicio: '#5eead4', admin_header_fim: '#0d9488' } },
  { nome: 'Dourado Suave',  emoji: '🌼', cores: { primaria: '#fbbf24', primaria_escura: '#d97706', admin_header_inicio: '#fcd34d', admin_header_fim: '#d97706' } },
  // ── Escuras (8) ─────────────────────────────────────────────────────────────
  { nome: 'Verde Floresta', emoji: '🌲', cores: { primaria: '#0f5a30', primaria_escura: '#106311', admin_header_inicio: '#348d52', admin_header_fim: '#0d5724' } },
  { nome: 'Azul Meia-Noite',emoji: '🌌', cores: { primaria: '#1e3a8a', primaria_escura: '#172554', admin_header_inicio: '#1e40af', admin_header_fim: '#172554' } },
  { nome: 'Roxo Profundo',  emoji: '🔮', cores: { primaria: '#581c87', primaria_escura: '#3b0764', admin_header_inicio: '#6b21a8', admin_header_fim: '#3b0764' } },
  { nome: 'Bordô Clássico', emoji: '🍷', cores: { primaria: '#881337', primaria_escura: '#4c0519', admin_header_inicio: '#9f1239', admin_header_fim: '#4c0519' } },
  { nome: 'Terracota Escuro',emoji: '🧱', cores: { primaria: '#7c2d12', primaria_escura: '#431407', admin_header_inicio: '#9a3412', admin_header_fim: '#431407' } },
  { nome: 'Vermelho Sangue',emoji: '🩸', cores: { primaria: '#7f1d1d', primaria_escura: '#450a0a', admin_header_inicio: '#991b1b', admin_header_fim: '#450a0a' } },
  { nome: 'Petróleo Escuro',emoji: '🌊', cores: { primaria: '#134e4a', primaria_escura: '#042f2e', admin_header_inicio: '#115e59', admin_header_fim: '#042f2e' } },
  { nome: 'Grafite Preto',  emoji: '⚫', cores: { primaria: '#27272a', primaria_escura: '#09090b', admin_header_inicio: '#52525b', admin_header_fim: '#09090b' } },
];

const MARCA_ICONES_OPCOES = ['🤸‍♀', '🧘', '💪', '🏃‍♀', '⚡', '🌟', '✨', '🎯', '🏋️', '🤸', '🌸', '🦋', '💎', '🌺', '🍃', '📅'];
// Mesmo catálogo de utils/siteBranding.js (frontend do tenant) — duplicado aqui
// porque master e tenant são dois apps React separados, sem código compartilhado.
const MARCA_FONTES_DISPONIVEIS = [
  { id: 'Inter',           label: 'Inter (padrão)', pilha: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Poppins',         label: 'Poppins',        pilha: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Roboto',          label: 'Roboto',         pilha: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Montserrat',      label: 'Montserrat',     pilha: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Nunito',          label: 'Nunito',         pilha: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Lato',            label: 'Lato',           pilha: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Quicksand',       label: 'Quicksand',      pilha: "'Quicksand', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" },
  { id: 'Playfair Display',label: 'Playfair Display (elegante)', pilha: "'Playfair Display', Georgia, 'Times New Roman', serif" },
];
const MARCA_FAVICON_OPCOES = ['📅', '🤸‍♀', '🧘', '💪', '⚡', '🌟', '✨', '🎯', '🌸', '🦋', '💎', '🌺', '🍃', '🏋️', '🤸'];
const MARCA_MAX_WIDTHS = ['1200px', '1400px', '1600px', '100%'];
const MARCA_LOGIN_BACKGROUNDS = [
  { nome: 'Verde Claro', inicio: '#f0fdf4', fim: '#dcfce7' },
  { nome: 'Azul Claro',  inicio: '#eff6ff', fim: '#dbeafe' },
  { nome: 'Roxo Claro',  inicio: '#faf5ff', fim: '#ede9fe' },
  { nome: 'Rosa Claro',  inicio: '#fff1f2', fim: '#ffe4e6' },
  { nome: 'Bege Quente', inicio: '#fffbeb', fim: '#fef3c7' },
  { nome: 'Vermelho Claro',   inicio: '#fef2f2', fim: '#fee2e2' },
  { nome: 'Turquesa Claro',   inicio: '#f0fdfa', fim: '#ccfbf1' },
  { nome: 'Amarelo Claro',    inicio: '#fefce8', fim: '#fef9c3' },
  { nome: 'Céu Claro',        inicio: '#f0f9ff', fim: '#e0f2fe' },
  { nome: 'Esmeralda Claro',  inicio: '#ecfdf5', fim: '#d1fae5' },
  { nome: 'Índigo Claro',     inicio: '#eef2ff', fim: '#e0e7ff' },
  { nome: 'Bordô Claro',      inicio: '#ffe4e6', fim: '#fecdd3' },
  { nome: 'Terracota Claro',  inicio: '#fff7ed', fim: '#ffedd5' },
  { nome: 'Cinza Claro',      inicio: '#f8fafc', fim: '#f1f5f9' },
  { nome: 'Branco Puro', inicio: '#ffffff', fim: '#f8fafc' },
];

function marcaDeepMerge(base, override) {
  const result = { ...base };
  for (const key in override) {
    if (override[key] && typeof override[key] === 'object' && !Array.isArray(override[key])) {
      result[key] = marcaDeepMerge(base[key] || {}, override[key]);
    } else if (override[key] !== undefined) {
      result[key] = override[key];
    }
  }
  return result;
}

function useMobileSimples() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}

const MarcaColorField = ({ label, value, onChange }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569' }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '6px 10px' }}>
      <div style={{ position: 'relative', width: '32px', height: '32px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0, border: '2px solid #e2e8f0' }}>
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          style={{ position: 'absolute', top: '-4px', left: '-4px', width: '40px', height: '40px', border: 'none', cursor: 'pointer', padding: 0 }} />
      </div>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        style={{ flex: 1, border: 'none', background: 'transparent', fontSize: '0.82rem', fontFamily: 'monospace', color: '#334155', outline: 'none' }}
        maxLength={7} />
    </div>
  </div>
);

const MarcaToggle = ({ label, description, value, onChange }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
    <div>
      <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>{label}</div>
      {description && <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{description}</div>}
    </div>
    <button onClick={() => onChange(!value)} style={{
      width: '46px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer',
      background: value ? '#0f5a30' : '#cbd5e1', position: 'relative', transition: 'all 0.2s ease', flexShrink: 0
    }}>
      <span style={{
        position: 'absolute', top: '3px', width: '20px', height: '20px', borderRadius: '50%',
        background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        left: value ? '23px' : '3px', transition: 'left 0.2s ease'
      }} />
    </button>
  </div>
);

const MarcaSectionTitle = ({ children }) => (
  <h3 style={{ margin: '0 0 20px', fontSize: '0.85rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px' }}>
    {children}
  </h3>
);

const MarcaInfoBox = ({ color = 'blue', children }) => {
  const colors = {
    blue:   { bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af' },
    green:  { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534' },
    yellow: { bg: '#fffbeb', border: '#fde68a', text: '#92400e' },
    red:    { bg: '#fef2f2', border: '#fecaca', text: '#991b1b' }
  };
  const c = colors[color];
  return (
    <div style={{ background: c.bg, borderRadius: '12px', padding: '14px 16px', border: `1px solid ${c.border}`, fontSize: '0.82rem', color: c.text }}>
      {children}
    </div>
  );
};

function AbaMarca({ api, tenantId }) {
  const isMobile = useMobileSimples();
  const [config, setConfig] = useState(MARCA_DEFAULT_CONFIG);
  const [secaoAtiva, setSecaoAtiva] = useState('identidade');
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [toast, setToast] = useState(null);
  const [alterado, setAlterado] = useState(false);

  useEffect(() => {
    api.get(`/master/tenants/${tenantId}/site/configuracoes`).then(res => {
      if (res.data.configuracoes && Object.keys(res.data.configuracoes).length > 0) {
        setConfig(marcaDeepMerge(MARCA_DEFAULT_CONFIG, res.data.configuracoes));
      }
    }).catch(() => {}).finally(() => setCarregando(false));
  }, [api, tenantId]);

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 3500); return () => clearTimeout(t); }
  }, [toast]);

  const atualizar = useCallback((secao, campo, valor) => {
    setConfig(c => ({ ...c, [secao]: { ...c[secao], [campo]: valor } }));
    setAlterado(true);
  }, []);

  const aplicarPaleta = (paleta) => {
    setConfig(c => ({ ...c, cores: { ...c.cores, ...paleta.cores } }));
    setAlterado(true);
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await api.put(`/master/tenants/${tenantId}/site/configuracoes`, { configuracoes: config });
      setToast({ type: 'success', message: '✅ Marca salva com sucesso!' });
      setAlterado(false);
    } catch {
      setToast({ type: 'error', message: '❌ Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const restaurarPadroes = () => {
    setConfig(MARCA_DEFAULT_CONFIG);
    setAlterado(true);
  };

  const secoes = [
    { id: 'identidade', label: 'Identidade',  icon: '🏷️' },
    { id: 'cores',      label: 'Cores',        icon: '🎨' },
    { id: 'login',      label: 'Tela de Login',icon: '🔐' },
    { id: 'favicon',    label: 'Favicon',      icon: '🌐' },
    { id: 'calendario', label: 'Calendário',   icon: '📅' },
    { id: 'desktop',    label: 'Desktop',      icon: '🖥️' },
    { id: 'mobile',     label: 'Mobile',       icon: '📱' }
  ];

  if (carregando) return <p>Carregando...</p>;

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", color: '#1e293b', background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
          background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          color: toast.type === 'success' ? '#166534' : '#991b1b',
          padding: '12px 20px', borderRadius: '12px', fontWeight: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)', fontSize: '0.875rem'
        }}>{toast.message}</div>
      )}

      <div style={{ padding: isMobile ? '16px' : '20px 28px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '1.05rem' : '1.2rem', fontWeight: 700 }}>🎨 Personalização do Portal</h2>
          <p style={{ margin: '3px 0 0', fontSize: '0.8rem', color: '#64748b' }}>Cores, identidade visual, tela de login, favicon, calendário e layout — o mesmo que o admin do tenant edita em "Personalização", aqui operado à distância.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={restaurarPadroes} style={{ padding: '8px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}>
            ↺ Padrões
          </button>
          <button onClick={salvar} disabled={salvando || !alterado} style={{
            padding: '8px 18px', borderRadius: '10px', border: 'none',
            background: alterado ? '#0f5a30' : '#e2e8f0',
            color: alterado ? 'white' : '#94a3b8', fontSize: '0.8rem', fontWeight: 700,
            cursor: alterado ? 'pointer' : 'not-allowed', transition: 'all 0.2s ease',
          }}>
            {salvando ? 'Salvando...' : '💾 Salvar'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: '520px' }}>

        <div style={{
          width: isMobile ? '100%' : '190px', flexShrink: 0,
          borderRight: isMobile ? 'none' : '1px solid #f1f5f9',
          borderBottom: isMobile ? '1px solid #f1f5f9' : 'none',
          background: '#fafbfc',
          display: 'flex', flexDirection: isMobile ? 'row' : 'column',
          overflowX: isMobile ? 'auto' : 'visible',
          padding: isMobile ? '8px' : '12px 8px'
        }}>
          {secoes.map(s => (
            <button key={s.id} onClick={() => setSecaoAtiva(s.id)} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: isMobile ? '9px 14px' : '10px 12px',
              borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: secaoAtiva === s.id ? '#0f5a30' : 'transparent',
              color: secaoAtiva === s.id ? 'white' : '#475569',
              fontSize: '0.82rem', fontWeight: secaoAtiva === s.id ? 700 : 500,
              whiteSpace: 'nowrap', transition: 'all 0.15s ease',
              marginBottom: isMobile ? 0 : '2px', marginRight: isMobile ? '4px' : 0,
              flexShrink: 0, textAlign: 'left'
            }}>
              <span>{s.icon}</span>{s.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, padding: isMobile ? '16px' : '24px 28px', overflowY: 'auto' }}>

          {secaoAtiva === 'identidade' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '520px' }}>
              <MarcaSectionTitle>Identidade Visual</MarcaSectionTitle>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Logo (PNG ou SVG)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '52px', height: '52px', borderRadius: '10px', border: '1.5px dashed #cbd5e1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', overflow: 'hidden', flexShrink: 0
                  }}>
                    {config.cabecalho.logo_url
                      ? <img src={config.cabecalho.logo_url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      : <span style={{ fontSize: '1.4rem' }}>{config.cabecalho.icone}</span>}
                  </div>
                  <label style={{ ...S.btnGhost, cursor: 'pointer' }}>
                    Enviar imagem
                    <input type="file" accept="image/png,image/svg+xml,image/webp" style={{ display: 'none' }}
                      onChange={async e => {
                        try {
                          const base64 = await lerImagemComoBase64(e.target.files[0]);
                          if (base64) atualizar('cabecalho', 'logo_url', base64);
                        } catch (err) {
                          setToast({ type: 'error', message: `❌ ${err.message}` });
                        } finally {
                          e.target.value = '';
                        }
                      }} />
                  </label>
                  {config.cabecalho.logo_url && (
                    <button type="button" onClick={() => atualizar('cabecalho', 'logo_url', '')} style={S.btnGhost}>Remover</button>
                  )}
                </div>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>
                  Se enviada, a logo substitui o ícone abaixo no cabeçalho e na tela de login. PNG ou SVG, até 300KB.
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Ícone do Logotipo (usado se não houver logo)</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {MARCA_ICONES_OPCOES.map(ic => (
                    <button key={ic} onClick={() => atualizar('cabecalho', 'icone', ic)} style={{
                      width: '44px', height: '44px', borderRadius: '10px', fontSize: '1.4rem',
                      border: config.cabecalho.icone === ic ? '2.5px solid #0f5a30' : '2px solid #e2e8f0',
                      background: config.cabecalho.icone === ic ? '#f0fdf4' : 'white',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
                    }}>{ic}</button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Título do Site</label>
                <input type="text" value={config.cabecalho.titulo} onChange={e => atualizar('cabecalho', 'titulo', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="Nome do negócio do cliente" />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Subtítulo / Tagline</label>
                <input type="text" value={config.cabecalho.subtitulo} onChange={e => atualizar('cabecalho', 'subtitulo', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="Slogan ou descrição curta" />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>E-mail de Suporte (rodapé "Contato")</label>
                <input type="email" value={config.contato?.email_suporte || ''} onChange={e => atualizar('contato', 'email_suporte', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="contato@negociodocliente.com.br" />
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>Se vazio, o link "Contato" fica oculto.</p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Fonte do Site</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {MARCA_FONTES_DISPONIVEIS.map(f => (
                    <button key={f.id} onClick={() => atualizar('tipografia', 'fonte', f.id)} style={{
                      padding: '10px 16px', borderRadius: '10px', fontSize: '0.9rem', fontFamily: f.pilha,
                      border: config.tipografia?.fonte === f.id ? '2.5px solid #0f5a30' : '2px solid #e2e8f0',
                      background: config.tipografia?.fonte === f.id ? '#f0fdf4' : 'white',
                      cursor: 'pointer', transition: 'all 0.15s'
                    }}>{f.label}</button>
                  ))}
                </div>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>Aplicada em todo o sistema do tenant — título, textos e botões.</p>
              </div>

              <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '16px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '10px', fontWeight: 700, letterSpacing: '0.05em' }}>PRÉVIA DO CABEÇALHO</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: config.cores.header_fundo, padding: '12px 16px', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  {config.cabecalho.logo_url
                    ? <img src={config.cabecalho.logo_url} alt="Logo" style={{ height: '28px', maxWidth: '120px', objectFit: 'contain' }} />
                    : <span style={{ fontSize: '1.6rem' }}>{config.cabecalho.icone}</span>}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: config.cores.primaria }}>{config.cabecalho.titulo}</div>
                    <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '2px' }}>{config.cabecalho.subtitulo}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {secaoAtiva === 'cores' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '560px' }}>
              <MarcaSectionTitle>Paleta de Cores</MarcaSectionTitle>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Temas Predefinidos</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {MARCA_PALETAS.map(p => (
                    <button key={p.nome} onClick={() => aplicarPaleta(p)} title={p.nome} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                      padding: '10px 12px', borderRadius: '12px', border: '2px solid',
                      borderColor: config.cores.primaria === p.cores.primaria ? p.cores.primaria : '#e2e8f0',
                      background: config.cores.primaria === p.cores.primaria ? '#f8fafc' : 'white',
                      cursor: 'pointer', transition: 'all 0.15s', minWidth: '68px'
                    }}>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        {[p.cores.primaria, p.cores.primaria_escura, p.cores.admin_header_inicio].map((c, i) => (
                          <span key={i} style={{ width: '14px', height: '14px', borderRadius: '50%', background: c, display: 'block' }} />
                        ))}
                      </div>
                      <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{p.emoji} {p.nome}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
                <MarcaColorField label="Cor Principal"          value={config.cores.primaria}             onChange={v => atualizar('cores', 'primaria', v)} />
                <MarcaColorField label="Cor Principal Escura"   value={config.cores.primaria_escura}       onChange={v => atualizar('cores', 'primaria_escura', v)} />
                <MarcaColorField label="Fundo do App"           value={config.cores.fundo_app}             onChange={v => atualizar('cores', 'fundo_app', v)} />
                <MarcaColorField label="Fundo do Cabeçalho"     value={config.cores.header_fundo}          onChange={v => atualizar('cores', 'header_fundo', v)} />
                <MarcaColorField label="Header Admin (início)"  value={config.cores.admin_header_inicio}   onChange={v => atualizar('cores', 'admin_header_inicio', v)} />
                <MarcaColorField label="Header Admin (fim)"     value={config.cores.admin_header_fim}      onChange={v => atualizar('cores', 'admin_header_fim', v)} />
              </div>

              <div style={{ background: '#f8fafc', borderRadius: '14px', padding: '16px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: '12px', fontWeight: 700, letterSpacing: '0.05em' }}>PRÉVIA DOS ELEMENTOS</div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <button style={{ background: config.cores.primaria, color: 'white', border: 'none', borderRadius: '10px', padding: '9px 18px', fontWeight: 600, fontSize: '0.85rem', cursor: 'default' }}>Botão Principal</button>
                  <button style={{ background: 'white', color: config.cores.primaria, border: `2px solid ${config.cores.primaria}`, borderRadius: '10px', padding: '8px 18px', fontWeight: 600, fontSize: '0.85rem', cursor: 'default' }}>Botão Outline</button>
                  <div style={{ background: `linear-gradient(135deg,${config.cores.admin_header_inicio},${config.cores.admin_header_fim})`, padding: '8px 16px', borderRadius: '10px', color: 'white', fontWeight: 600, fontSize: '0.82rem' }}>Header Admin</div>
                </div>
              </div>
            </div>
          )}

          {secaoAtiva === 'login' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '560px' }}>
              <MarcaSectionTitle>Tela de Login</MarcaSectionTitle>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Fundo da Página de Login</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {MARCA_LOGIN_BACKGROUNDS.map(bg => (
                    <button key={bg.nome} onClick={() => {
                      setConfig(c => ({ ...c, login: { ...c.login, background_inicio: bg.inicio, background_fim: bg.fim } }));
                      setAlterado(true);
                    }} title={bg.nome} style={{
                      width: '52px', height: '40px', borderRadius: '10px', cursor: 'pointer',
                      background: `linear-gradient(135deg,${bg.inicio},${bg.fim})`,
                      border: config.login.background_inicio === bg.inicio ? '3px solid #0f5a30' : '2px solid #e2e8f0',
                      transition: 'all 0.15s'
                    }} />
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
                  <MarcaColorField label="Cor início" value={config.login.background_inicio} onChange={v => atualizar('login', 'background_inicio', v)} />
                  <MarcaColorField label="Cor fim"    value={config.login.background_fim}    onChange={v => atualizar('login', 'background_fim', v)} />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Título do Card de Login</label>
                <input type="text" value={config.login.titulo} onChange={e => atualizar('login', 'titulo', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="Acessar minha conta" />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Descrição / Boas-vindas</label>
                <textarea value={config.login.descricao} onChange={e => atualizar('login', 'descricao', e.target.value)} rows={3}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Texto de boas-vindas exibido na tela de login" />
              </div>

              <div style={{ background: `linear-gradient(160deg,${config.login.background_inicio},${config.login.background_fim})`, borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '12px', fontWeight: 700, letterSpacing: '0.05em' }}>PRÉVIA DA TELA DE LOGIN</div>
                <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                  <div style={{ fontSize: '2rem' }}>{config.cabecalho.icone}</div>
                  <div style={{ fontWeight: 800, fontSize: '1.2rem', color: config.cores.primaria, margin: '4px 0 2px' }}>{config.cabecalho.titulo}</div>
                  <div style={{ fontSize: '0.75rem', color: '#374151', lineHeight: 1.5 }}>{config.login.descricao?.slice(0, 80)}...</div>
                </div>
                <div style={{ background: 'white', borderRadius: '12px', padding: '12px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                  <div style={{ background: `linear-gradient(135deg,${config.cores.admin_header_inicio},${config.cores.admin_header_fim})`, borderRadius: '8px 8px 0 0', margin: '-12px -16px 12px', padding: '10px', color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>{config.login.titulo}</div>
                  <div style={{ background: '#f1f5f9', borderRadius: '8px', padding: '8px 12px', fontSize: '0.75rem', color: '#475569' }}>🔐 Entrar com Google</div>
                </div>
              </div>
            </div>
          )}

          {secaoAtiva === 'favicon' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '480px' }}>
              <MarcaSectionTitle>Favicon (Ícone da Aba)</MarcaSectionTitle>

              <MarcaInfoBox color="blue">
                <strong>💡 Dica:</strong> O favicon aparece na aba do navegador e ao salvar o site na tela inicial do celular. Use um emoji simples e reconhecível, envie um arquivo, ou cole a URL de uma imagem .ico ou .png (32×32px).
              </MarcaInfoBox>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Escolher Emoji como Favicon</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {MARCA_FAVICON_OPCOES.map(em => (
                    <button key={em} onClick={() => { atualizar('favicon', 'emoji', em); atualizar('favicon', 'url', ''); }} style={{
                      width: '48px', height: '48px', borderRadius: '10px', fontSize: '1.5rem',
                      border: config.favicon.emoji === em && !config.favicon.url ? '2.5px solid #0f5a30' : '2px solid #e2e8f0',
                      background: config.favicon.emoji === em && !config.favicon.url ? '#f0fdf4' : 'white',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s'
                    }}>{em}</button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Ou Envie um Arquivo (.ico, .png, .svg)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ ...S.btnGhost, cursor: 'pointer' }}>
                    Escolher arquivo
                    <input type="file" accept="image/x-icon,image/png,image/svg+xml,.ico" style={{ display: 'none' }}
                      onChange={async e => {
                        try {
                          const base64 = await lerImagemComoBase64(e.target.files[0]);
                          if (base64) atualizar('favicon', 'url', base64);
                        } catch (err) {
                          setToast({ type: 'error', message: `❌ ${err.message}` });
                        } finally {
                          e.target.value = '';
                        }
                      }} />
                  </label>
                  {config.favicon.url && (
                    <button type="button" onClick={() => atualizar('favicon', 'url', '')} style={S.btnGhost}>Remover</button>
                  )}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>Ou Cole a URL de uma Imagem</label>
                <input type="url" value={config.favicon.url?.startsWith('data:') ? '' : config.favicon.url}
                  onChange={e => atualizar('favicon', 'url', e.target.value)}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
                  placeholder="https://seusite.com/favicon.ico" />
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '6px' }}>Arquivo enviado ou URL — o que for preenchido por último sobrepõe o emoji acima.</p>
              </div>

              <div style={{ background: '#1e293b', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: '#334155', borderRadius: '8px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.1rem' }}>{config.favicon.url ? '🖼️' : config.favicon.emoji}</span>
                  <span style={{ color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 500 }}>{config.cabecalho.titulo}</span>
                  <span style={{ color: '#64748b', fontSize: '0.8rem' }}>×</span>
                </div>
                <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>← Prévia da aba do navegador</span>
              </div>
            </div>
          )}

          {secaoAtiva === 'calendario' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '560px' }}>
              <MarcaSectionTitle>Integração Google Agenda</MarcaSectionTitle>

              <MarcaInfoBox color="green">
                <strong>✅ Calendário da academia:</strong> Sincronizado automaticamente pelo servidor via Service Account — funciona sempre, independente desta configuração.
              </MarcaInfoBox>

              <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div onClick={() => atualizar('calendario', 'modo', 'link')} style={{
                  padding: '18px 20px', cursor: 'pointer',
                  background: config.calendario.modo === 'link' ? '#f0fdf4' : 'white',
                  borderLeft: config.calendario.modo === 'link' ? '4px solid #0f5a30' : '4px solid transparent',
                  borderBottom: '1px solid #f1f5f9'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%', border: '2px solid',
                      borderColor: config.calendario.modo === 'link' ? '#0f5a30' : '#cbd5e1',
                      background: config.calendario.modo === 'link' ? '#0f5a30' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
                    }}>
                      {config.calendario.modo === 'link' && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white', display: 'block' }} />}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: '4px' }}>
                        📆 Link Google Agenda <span style={{ background: '#dcfce7', color: '#166534', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', marginLeft: '6px' }}>Recomendado</span>
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6 }}>
                        Após agendar, o cliente vê um botão <strong>"📆 Adicionar ao Google Agenda"</strong> que abre o app do Google Agenda com o evento já preenchido. <strong>Não requer permissões especiais do Google, sem aviso de "app não verificado".</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div onClick={() => atualizar('calendario', 'modo', 'oauth')} style={{
                  padding: '18px 20px', cursor: 'pointer',
                  background: config.calendario.modo === 'oauth' ? '#eff6ff' : 'white',
                  borderLeft: config.calendario.modo === 'oauth' ? '4px solid #2563eb' : '4px solid transparent'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                    <div style={{
                      width: '20px', height: '20px', borderRadius: '50%', border: '2px solid',
                      borderColor: config.calendario.modo === 'oauth' ? '#2563eb' : '#cbd5e1',
                      background: config.calendario.modo === 'oauth' ? '#2563eb' : 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
                    }}>
                      {config.calendario.modo === 'oauth' && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'white', display: 'block' }} />}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: '4px' }}>
                        🔐 OAuth Automático <span style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', marginLeft: '6px' }}>Requer verificação Google</span>
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.6 }}>
                        O sistema solicita permissão ao cliente no primeiro login e adiciona o evento automaticamente no Google Agenda pessoal. <strong>Exige que o app passe pela verificação formal do Google</strong> (processo de 1 a 6 semanas).
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {config.calendario.modo === 'oauth' && (
                <MarcaInfoBox color="yellow">
                  <strong>⚠️ Atenção:</strong> Com OAuth ativo e app não verificado pelo Google, os clientes verão um aviso de "informações confidenciais" ao fazer login.
                </MarcaInfoBox>
              )}
              {config.calendario.modo === 'link' && (
                <MarcaInfoBox color="green">
                  <strong>✅ Configuração segura:</strong> Nenhum aviso do Google será exibido.
                </MarcaInfoBox>
              )}
            </div>
          )}

          {secaoAtiva === 'desktop' && (
            <div style={{ maxWidth: '480px' }}>
              <MarcaSectionTitle>Layout — Desktop</MarcaSectionTitle>
              <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '8px 16px', marginBottom: '16px' }}>
                <MarcaToggle label="Cabeçalho fixo" description="Mantém o header visível ao rolar a página" value={config.layout.cabecalho_fixo} onChange={v => atualizar('layout', 'cabecalho_fixo', v)} />
                <MarcaToggle label="Mostrar rodapé" description="Exibe o footer com links e créditos" value={config.layout.mostrar_footer} onChange={v => atualizar('layout', 'mostrar_footer', v)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '10px' }}>Largura Máxima do Conteúdo</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {MARCA_MAX_WIDTHS.map(w => (
                    <button key={w} onClick={() => atualizar('layout', 'desktop_max_width', w)} style={{
                      padding: '8px 18px', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 600,
                      border: '2px solid', cursor: 'pointer',
                      borderColor: config.layout.desktop_max_width === w ? '#0f5a30' : '#e2e8f0',
                      background: config.layout.desktop_max_width === w ? '#0f5a30' : 'white',
                      color: config.layout.desktop_max_width === w ? 'white' : '#475569'
                    }}>{w}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {secaoAtiva === 'mobile' && (
            <div style={{ maxWidth: '480px' }}>
              <MarcaSectionTitle>Layout — Mobile</MarcaSectionTitle>
              <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '8px 16px', marginBottom: '20px' }}>
                <MarcaToggle label="Nome do usuário no menu" description="Exibe o nome completo no menu hamburguer" value={config.mobile.mostrar_nome_no_menu} onChange={v => atualizar('mobile', 'mostrar_nome_no_menu', v)} />
              </div>
              <MarcaInfoBox color="blue">
                <strong>💡 Dica:</strong> Alterações de cores e identidade visual se aplicam automaticamente ao mobile. Use as abas <strong>Identidade</strong> e <strong>Cores</strong> para personalizar elementos comuns.
              </MarcaInfoBox>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function AbaDominio({ api, tenant }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);

  const configurar = async () => {
    setLoading(true); setResultado(null);
    try {
      const { data } = await api.post(`/master/tenants/${tenant.id}/dominio/configurar`, { email: email || undefined });
      setResultado({ ok: true, log: data.log });
    } catch (err) {
      setResultado({ ok: false, error: err.response?.data?.error || 'Erro ao configurar domínio', log: err.response?.data?.log });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.card}>
      <h3 style={{ marginTop: 0 }}>🌐 Domínio (Nginx + SSL)</h3>
      <p style={{ fontSize: '0.85rem', color: '#475569' }}>
        Cria/atualiza o vhost Nginx de <strong>{tenant.dominio}</strong> (proxy pra API do
        tenant + arquivos estáticos) e emite o certificado SSL via certbot. Exige que o DNS
        do domínio já esteja apontando pra este servidor — se ainda não estiver, a operação
        falha na etapa do certbot e pode ser tentada de novo aqui a qualquer momento.
      </p>
      <label style={S.label}>E-mail para o certbot (opcional)</label>
      <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="usa o e-mail padrão da plataforma se vazio" />
      <button style={{ ...S.btn, marginTop: '14px' }} onClick={configurar} disabled={loading}>
        {loading ? 'Configurando...' : 'Configurar Nginx + SSL (certbot)'}
      </button>
      {resultado && (
        <div style={{ marginTop: '14px' }}>
          <div style={resultado.ok ? S.success : S.error}>
            {resultado.ok ? '✅ Domínio configurado com sucesso.' : `❌ ${resultado.error}`}
          </div>
          {resultado.log && (
            <pre style={{
              background: '#0f172a', color: '#e2e8f0', padding: '12px', borderRadius: '8px',
              fontSize: '0.72rem', overflowX: 'auto', whiteSpace: 'pre-wrap',
            }}>{resultado.log}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function TenantDetail({ api, tenant, onVoltar }) {
  const [aba, setAba] = useState('marca');
  return (
    <div>
      <button style={{ ...S.btnGhost, marginBottom: '16px' }} onClick={onVoltar}>← Voltar</button>
      <h2 style={{ marginTop: 0 }}>{tenant.nome} <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '1rem' }}>({tenant.dominio})</span></h2>
      <div style={S.tabs}>
        <button style={S.tab(aba === 'marca')} onClick={() => setAba('marca')}>🎨 Marca</button>
        <button style={S.tab(aba === 'usuarios')} onClick={() => setAba('usuarios')}>👥 Usuários</button>
        <button style={S.tab(aba === 'integracoes')} onClick={() => setAba('integracoes')}>🔌 Integrações</button>
        <button style={S.tab(aba === 'bot')} onClick={() => setAba('bot')}>🤖 Bot</button>
        <button style={S.tab(aba === 'sistema')} onClick={() => setAba('sistema')}>⚙️ Sistema</button>
        <button style={S.tab(aba === 'logs')} onClick={() => setAba('logs')}>📋 Logs</button>
        <button style={S.tab(aba === 'dominio')} onClick={() => setAba('dominio')}>🌐 Domínio</button>
      </div>
      {aba === 'marca' && <AbaMarca api={api} tenantId={tenant.id} />}
      {aba === 'usuarios' && <AbaUsuarios api={api} tenantId={tenant.id} />}
      {aba === 'integracoes' && <AbaIntegracoes api={api} tenantId={tenant.id} />}
      {aba === 'bot' && <AbaBot api={api} tenantId={tenant.id} />}
      {aba === 'sistema' && <AbaSistema api={api} tenantId={tenant.id} />}
      {aba === 'logs' && <AbaLogs api={api} tenantId={tenant.id} />}
      {aba === 'dominio' && <AbaDominio api={api} tenant={tenant} />}
    </div>
  );
}

export default function App() {
  const [admin, setAdmin] = useState(null);
  const [tenantAberto, setTenantAberto] = useState(null);
  const [carregandoSessao, setCarregandoSessao] = useState(true);

  const api = createApi(getToken());

  useEffect(() => {
    const token = getToken();
    if (!token) { setCarregandoSessao(false); return; }
    createApi(token).get('/master/auth/me')
      .then(({ data }) => setAdmin(data.admin))
      .catch(() => clearToken())
      .finally(() => setCarregandoSessao(false));
  }, []);

  const sair = () => { clearToken(); setAdmin(null); setTenantAberto(null); };

  if (carregandoSessao) return null;
  if (!admin) return <LoginScreen onLogin={setAdmin} />;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <strong>🛠️ Agenda SaaS — Master</strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>{admin.nome}</span>
          <button style={{ ...S.btnGhost, background: 'transparent', color: '#fff', borderColor: '#374151' }} onClick={sair}>Sair</button>
        </div>
      </div>
      <div style={S.main}>
        {tenantAberto
          ? <TenantDetail api={api} tenant={tenantAberto} onVoltar={() => setTenantAberto(null)} />
          : <TenantList api={api} onAbrir={setTenantAberto} />}
      </div>
    </div>
  );
}
